import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
// M3a: relative-import the in-app modules — see App.tsx for the
// vibe-designer alias-collision rationale.
import type { BriefingRoomServices, HostMode } from '../services/types';
import { detectHost } from '../services/hostDetection';
import { VoiceServiceModule } from '../services/host/VoiceServiceModule';
import { LlmServiceModule } from '../services/host/LlmServiceModule';
import { McpServiceModule } from '../services/host/McpServiceModule';
import { AuthServiceModule } from '../services/host/AuthServiceModule';
import { PersistenceServiceModule } from '../services/host/PersistenceServiceModule';
import { GmOrchestratorServiceModule } from '../services/host/GmOrchestratorServiceModule';
import { SpaContextServiceModule } from '../services/host/SpaContextServiceModule';
import { VoiceServiceStandalone } from '../services/standalone/VoiceServiceStandalone';
import { LlmServiceStandalone } from '../services/standalone/LlmServiceStandalone';
import { McpServiceStandalone } from '../services/standalone/McpServiceStandalone';
import { AuthServiceStandalone } from '../services/standalone/AuthServiceStandalone';
import { PersistenceServiceStandalone } from '../services/standalone/PersistenceServiceStandalone';
import { GmOrchestratorServiceStandalone } from '../services/standalone/GmOrchestratorServiceStandalone';
import { SpaContextServiceStandalone } from '../services/standalone/SpaContextServiceStandalone';

const BriefingRoomServicesContext = createContext<BriefingRoomServices | null>(null);

/**
 * D-polish v1.2.3 (2026-06-02): retry hook context. Separate from the
 * services context so consumers can grab the retry function without
 * forcing a re-render whenever services changes. User feedback:
 * landed on the standalone placeholder after clicking Take the
 * Mission — host detection had timed out / raced at SPA boot. The
 * retry button on the placeholder calls this.
 */
const BriefingRoomRetryContext = createContext<(() => void) | null>(null);

function buildModuleServices(): BriefingRoomServices {
  return {
    host: 'module',
    voice: new VoiceServiceModule(),
    persistence: new PersistenceServiceModule(),
    llm: new LlmServiceModule(),
    gmOrchestrator: new GmOrchestratorServiceModule(),
    mcp: {
      mind: new McpServiceModule('mind'),
      sight: new McpServiceModule('sight'),
      cortex: new McpServiceModule('cortex'),
    },
    auth: new AuthServiceModule(),
    spaContext: new SpaContextServiceModule(),
  };
}

function buildStandaloneServices(): BriefingRoomServices {
  return {
    host: 'standalone',
    voice: new VoiceServiceStandalone(),
    persistence: new PersistenceServiceStandalone(),
    llm: new LlmServiceStandalone(),
    gmOrchestrator: new GmOrchestratorServiceStandalone(),
    mcp: {
      mind: new McpServiceStandalone(),
      sight: new McpServiceStandalone(),
      cortex: new McpServiceStandalone(),
    },
    auth: new AuthServiceStandalone(),
    spaContext: new SpaContextServiceStandalone(),
  };
}

export function buildServices(host: HostMode): BriefingRoomServices {
  return host === 'module' ? buildModuleServices() : buildStandaloneServices();
}

export interface BriefingRoomProviderProps {
  services?: BriefingRoomServices;
  children: ReactNode;
}

/**
 * BriefingRoomProvider — Module-Host M3a.
 *
 * Auto-detects the host on mount via detectHost() then instantiates the
 * appropriate service implementations. A pre-built `services` prop
 * overrides detection — used by:
 *   (a) the host (vibe-designer) when it wants to inject pre-bound
 *       services rather than have the SPA detect them, and
 *   (b) vitest to inject mock services without running detectHost.
 *
 * The first render shows a null services value (loading). Children that
 * call useBriefingRoomServices before resolution get null — the host
 * pattern is to wrap children in a Suspense-style guard or branch on
 * the null value.
 */
export function BriefingRoomProvider({ services, children }: BriefingRoomProviderProps) {
  const [autoServices, setAutoServices] = useState<BriefingRoomServices | null>(
    services ?? null,
  );
  // D-polish v1.2.3: bump this nonce to re-trigger the detection effect.
  // Default 0; the retry callback increments it.
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (services !== undefined) {
      setAutoServices(services);
      return;
    }
    let cancelled = false;
    // Reset services to null so consumers re-enter the loading state
    // while we re-detect. Without this the picker would keep showing
    // the stale 'standalone' placeholder during the retry round-trip.
    if (retryNonce > 0) {
      setAutoServices(null);
    }
    void (async () => {
      const host = await detectHost();
      if (!cancelled) {
        setAutoServices(buildServices(host));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [services, retryNonce]);

  const value = useMemo(() => autoServices, [autoServices]);

  // D-polish v1.2.3: stable retry callback exposed via context so
  // children (specifically the picker's standalone placeholder) can
  // re-trigger host detection without a full page reload.
  const retry = useCallback(() => {
    if (services !== undefined) {
      // Caller injected services explicitly — retry is a no-op (tests
      // wire services directly and don't want detection re-run).
      return;
    }
    setRetryNonce((n) => n + 1);
  }, [services]);

  return (
    <BriefingRoomServicesContext.Provider value={value}>
      <BriefingRoomRetryContext.Provider value={retry}>
        {children}
      </BriefingRoomRetryContext.Provider>
    </BriefingRoomServicesContext.Provider>
  );
}

export function useBriefingRoomServices(): BriefingRoomServices {
  const ctx = useContext(BriefingRoomServicesContext);
  if (ctx === null) {
    throw new Error(
      'useBriefingRoomServices: no BriefingRoomProvider found in the tree. ' +
        'Wrap the consumer in <BriefingRoomProvider />.',
    );
  }
  return ctx;
}

export function useOptionalBriefingRoomServices(): BriefingRoomServices | null {
  return useContext(BriefingRoomServicesContext);
}

/**
 * D-polish v1.2.3: returns a callback that re-runs host detection when
 * called. Used by the picker's standalone placeholder to recover from
 * a transient detection failure without a full page reload. Returns
 * null when no provider is in the tree (consumers should branch).
 */
export function useBriefingRoomRetryDetection(): (() => void) | null {
  return useContext(BriefingRoomRetryContext);
}

export { BriefingRoomServicesContext, BriefingRoomRetryContext };
